import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { compliance, vehicles } from '../api/methods';
import { getSessionEpoch, isCurrentSessionEpoch, StaleSessionError } from '../utils/sessionEpoch';
export const complianceKeys = {
    /** Prefix of every register query, and the key of every register mutation. */
    all: ['compliance'],
    scope: ['compliance', 'scope'],
    documentTypes: ['compliance', 'document-types'],
    employees: (params) => ['compliance', 'employees', params],
    records: ['compliance', 'records'],
    recordList: (params) => ['compliance', 'records', 'list', params],
    record: (recordId) => ['compliance', 'records', 'detail', recordId],
    matrix: (businessUnitId) => ['compliance', 'matrix', businessUnitId],
    contacts: (businessUnitId) => ['compliance', 'contacts', businessUnitId],
    contactCandidates: (businessUnitId) => ['compliance', 'contacts', businessUnitId, 'candidates'],
    settings: (businessUnitId) => ['compliance', 'settings', businessUnitId],
    myDocuments: ['compliance', 'me', 'documents'],
    vehicles: ['compliance', 'vehicles'],
    vehicleList: (params) => ['compliance', 'vehicles', 'list', params],
    vehicle: (vehicleId) => ['compliance', 'vehicles', 'detail', vehicleId],
};
/**
 * The register holds one person's licenses and certificates, and techs share
 * phones. Every identity boundary (sign-out, expired session, user switch:
 * the session epoch moving) removes all of it from the cache, so the next
 * person never renders the previous person's scope or documents.
 */
export function removeComplianceQueries(queryClient) {
    queryClient.removeQueries({ queryKey: complianceKeys.all });
}
/**
 * A request answered after the session changed never reaches the cache (as
 * useTicket), and neither does its error: a departed session's late 401 is a
 * StaleSessionError, so it cannot end the next person's register session.
 * Wraps every register query and mutation; the epoch is the one the request
 * started in.
 */
async function inSession(request) {
    const epoch = getSessionEpoch();
    let result;
    try {
        result = await request();
    }
    catch (err) {
        if (!isCurrentSessionEpoch(epoch))
            throw new StaleSessionError();
        throw err;
    }
    if (!isCurrentSessionEpoch(epoch))
        throw new StaleSessionError();
    return result;
}
/** The register's refresh after a write, unless the write got a 401. */
function isUnauthorized(err) {
    return err?.status === 401;
}
function isNotFound(err) {
    return !!err && typeof err === 'object' && err.status === 404;
}
/**
 * The register's access flags for the signed-in user. Drives both nav entries.
 * `null` means the feature is off (`compliance_register` flag → 404), which
 * hides everything. Any other failure is an error and also hides the entries.
 */
export function useComplianceScope(enabled = true) {
    return useQuery({
        queryKey: complianceKeys.scope,
        queryFn: () => inSession(async () => {
            try {
                const data = await compliance.scope();
                return {
                    companies: Array.isArray(data?.companies) ? data.companies : [],
                    self: data?.self && typeof data.self === 'object' ? data.self : null,
                };
            }
            catch (err) {
                if (isNotFound(err))
                    return null;
                throw err;
            }
        }),
        enabled,
        retry: false,
        staleTime: 5 * 60_000,
    });
}
export function useComplianceDocumentTypes(enabled = true) {
    return useQuery({
        queryKey: complianceKeys.documentTypes,
        queryFn: () => inSession(() => compliance.documentTypes().then((r) => r.document_types ?? [])),
        enabled,
        staleTime: 10 * 60_000,
    });
}
export function useComplianceEmployees(params, enabled = true) {
    return useQuery({
        queryKey: complianceKeys.employees(params),
        queryFn: () => inSession(() => compliance.employees(params).then((r) => r.employees ?? [])),
        enabled,
        staleTime: 5 * 60_000,
        placeholderData: keepPreviousData,
    });
}
export function useComplianceRecords(params, enabled = true) {
    return useQuery({
        queryKey: complianceKeys.recordList(params),
        queryFn: ({ signal }) => inSession(() => compliance.records(params, { signal })),
        enabled,
        placeholderData: keepPreviousData,
    });
}
export function useComplianceRecord(recordId) {
    return useQuery({
        queryKey: complianceKeys.record(recordId ?? ''),
        queryFn: () => inSession(() => compliance.record(recordId)),
        enabled: Boolean(recordId),
    });
}
export function useComplianceMatrix(businessUnitId) {
    return useQuery({
        queryKey: complianceKeys.matrix(businessUnitId ?? ''),
        queryFn: () => inSession(() => compliance.matrix(businessUnitId)),
        enabled: Boolean(businessUnitId),
    });
}
/**
 * Review decisions are never optimistic: the server re-checks scope and the
 * revision, so the UI shows the result only after it answers. Success and
 * failure both refetch, which is also how a 409 reloads the record.
 */
export function useReviewComplianceRecord() {
    const qc = useQueryClient();
    return useMutation({
        mutationKey: complianceKeys.all,
        mutationFn: ({ recordId, body }) => inSession(() => compliance.review(recordId, body)),
        // A 401 skips the refresh, so the session boundary clears at once
        // instead of waiting on refetches of private data.
        onSettled: (_data, err) => (isUnauthorized(err) ? undefined : qc.invalidateQueries({ queryKey: complianceKeys.all })),
    });
}
export function useArchiveComplianceRecord() {
    const qc = useQueryClient();
    return useMutation({
        mutationKey: complianceKeys.all,
        mutationFn: ({ recordId, body }) => inSession(() => compliance.archive(recordId, body)),
        onSettled: (_data, err) => (isUnauthorized(err) ? undefined : qc.invalidateQueries({ queryKey: complianceKeys.all })),
    });
}
export function usePreviewComplianceBatch() {
    return useMutation({
        mutationKey: complianceKeys.all,
        mutationFn: (payload) => inSession(() => compliance.previewBatch(payload)),
    });
}
export function useCommitComplianceBatch() {
    const qc = useQueryClient();
    return useMutation({
        mutationKey: complianceKeys.all,
        mutationFn: ({ payload, planHash, files, idempotencyKey, }) => inSession(() => compliance.commitBatch({ payload, planHash, files }, idempotencyKey)),
        onSettled: (_data, err) => (isUnauthorized(err) ? undefined : qc.invalidateQueries({ queryKey: complianceKeys.all })),
    });
}
export function useComplianceContacts(businessUnitId, enabled = true) {
    return useQuery({
        queryKey: complianceKeys.contacts(businessUnitId ?? ''),
        queryFn: () => inSession(() => compliance.contacts(businessUnitId)),
        enabled: enabled && Boolean(businessUnitId),
    });
}
export function useComplianceContactCandidates(businessUnitId, enabled = true) {
    return useQuery({
        queryKey: complianceKeys.contactCandidates(businessUnitId ?? ''),
        queryFn: () => inSession(() => compliance.contactCandidates(businessUnitId)),
        enabled: enabled && Boolean(businessUnitId),
        staleTime: 60_000,
    });
}
export function useSetComplianceContact() {
    const qc = useQueryClient();
    return useMutation({
        mutationKey: complianceKeys.all,
        mutationFn: (body) => inSession(() => compliance.setContact(body)),
        onSettled: (_data, err, body) => {
            if (isUnauthorized(err))
                return;
            qc.invalidateQueries({ queryKey: complianceKeys.contacts(body.business_unit_id) });
            // A contact change can change the caller's own scope.
            qc.invalidateQueries({ queryKey: complianceKeys.scope });
        },
    });
}
export function useRemoveComplianceContact() {
    const qc = useQueryClient();
    return useMutation({
        mutationKey: complianceKeys.all,
        mutationFn: (body) => inSession(() => compliance.removeContact(body)),
        onSettled: (_data, err, body) => {
            if (isUnauthorized(err))
                return;
            qc.invalidateQueries({ queryKey: complianceKeys.contacts(body.business_unit_id) });
            qc.invalidateQueries({ queryKey: complianceKeys.scope });
        },
    });
}
export function useComplianceSettings(businessUnitId, enabled = true) {
    return useQuery({
        queryKey: complianceKeys.settings(businessUnitId ?? ''),
        queryFn: () => inSession(() => compliance.settings(businessUnitId)),
        enabled: enabled && Boolean(businessUnitId),
    });
}
export function useSetComplianceSettings() {
    const qc = useQueryClient();
    return useMutation({
        mutationKey: complianceKeys.all,
        mutationFn: (body) => inSession(() => compliance.setSettings(body)),
        onSettled: (_data, err, body) => isUnauthorized(err) ? undefined : qc.invalidateQueries({ queryKey: complianceKeys.settings(body.business_unit_id) }),
    });
}
// ── Vehicles ─────────────────────────────────────────────────────────────────
export function useVehicles(params, enabled = true) {
    return useQuery({
        queryKey: complianceKeys.vehicleList(params),
        queryFn: () => inSession(() => vehicles.list(params).then((r) => r.vehicles ?? [])),
        enabled,
        placeholderData: keepPreviousData,
    });
}
export function useVehicle(vehicleId) {
    return useQuery({
        queryKey: complianceKeys.vehicle(vehicleId ?? ''),
        queryFn: () => inSession(() => vehicles.get(vehicleId)),
        enabled: Boolean(vehicleId),
    });
}
function useFleetMutation(mutationFn) {
    const qc = useQueryClient();
    return useMutation({
        mutationKey: complianceKeys.all,
        mutationFn: (vars) => inSession(() => mutationFn(vars)),
        // Returned so the mutation stays pending until the refresh lands: a
        // button gated on isPending cannot be pressed again against stale state
        // (a second "Returned" would 409). A 401 skips it (see review above).
        onSettled: (_data, err) => isUnauthorized(err) ? undefined : Promise.all([
            qc.invalidateQueries({ queryKey: complianceKeys.vehicles }),
            qc.invalidateQueries({ queryKey: complianceKeys.myDocuments }),
        ]),
    });
}
export function useCreateVehicle() {
    return useFleetMutation((fields) => vehicles.create(fields));
}
export function useUpdateVehicle() {
    return useFleetMutation(({ vehicleId, expectedRevision, fields }) => vehicles.update(vehicleId, expectedRevision, fields));
}
export function useAssignVehicle() {
    return useFleetMutation(({ vehicleId, body }) => vehicles.assign(vehicleId, body));
}
export function useEndVehicleAssignment() {
    return useFleetMutation(({ assignmentId, note }) => vehicles.endAssignment(assignmentId, note ? { note } : {}));
}
// ── Self-service ─────────────────────────────────────────────────────────────
export function useMyDocuments(enabled = true) {
    return useQuery({
        queryKey: complianceKeys.myDocuments,
        queryFn: () => inSession(() => compliance.myDocuments()),
        enabled,
        staleTime: 60_000,
    });
}
export function useCheckoutVehicle() {
    return useFleetMutation(({ vehicleId, body }) => vehicles.checkout(vehicleId, body));
}
export function useReturnVehicle() {
    return useFleetMutation((assignmentId) => vehicles.returnCheckout(assignmentId));
}
//# sourceMappingURL=useCompliance.js.map